import {agoString} from '@github-ui/ago'
import type {RecentlyTouchedBranch} from '@github-ui/code-view-types'
import type {Repository} from '@github-ui/current-repository'
import {branchPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-router'
import {useAlive} from '@github-ui/use-alive'
import {GitBranchIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash, LinkButton} from '@primer/react'
import {useCallback, useRef} from 'react'

import styles from './RecentlyTouchedBranches.module.css'
import {useRecentlyTouchedBranches} from '@github-ui/code-view-shared/contexts/RecentlyTouchedBranchesContext'

export function RecentlyTouchedBranches({repo}: {repo: Repository}) {
  const {branches, channel: aliveChannel, canCreatePullRequest, refetch} = useRecentlyTouchedBranches()

  if (!aliveChannel || !branches) {
    return null
  }

  return (
    <RecentlyTouchedBranchesInner
      branches={branches}
      canCreatePullRequest={canCreatePullRequest}
      channel={aliveChannel}
      fetchRecentlyTouchedBranches={refetch}
      repo={repo}
    />
  )
}

export function RecentlyTouchedBranchesInner({
  branches,
  canCreatePullRequest = true,
  channel,
  fetchRecentlyTouchedBranches,
  repo,
}: {
  branches: RecentlyTouchedBranch[]
  canCreatePullRequest?: boolean
  channel: string
  fetchRecentlyTouchedBranches: () => void
  repo: Repository
}) {
  const timeout = useRef<number | null>(null)

  const handleAliveEvent = useCallback(() => {
    // We get a lot of events for one push, so we debounce them.
    if (timeout.current !== null) {
      window.clearTimeout(timeout.current)
    }

    timeout.current = window.setTimeout(() => {
      fetchRecentlyTouchedBranches()
    }, 500)
  }, [fetchRecentlyTouchedBranches])

  useAlive(channel, handleAliveEvent)

  if (!branches) {
    return null
  }

  return (
    <>
      {branches.map((branch, index) => (
        <Flash
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={branch.branchName + index}
          variant="warning"
          className={styles.Flash}
        >
          <div className={styles.Box}>
            <GitBranchIcon className={styles.Octicon} />
            <Link
              to={branchPath({owner: branch.repoOwner, repo: branch.repoName, branch: branch.branchName})}
              reloadDocument
              className="text-bold"
            >
              {repo.ownerLogin !== branch.repoOwner ? `${branch.repoOwner}:` : ''}
              {branch.branchName}
            </Link>
            {` had recent pushes ${agoString(new Date(branch.date))}`}
          </div>

          <LinkButton href={branch.comparePath} variant="primary">
            {canCreatePullRequest ? 'Compare & pull request' : 'Compare'}
          </LinkButton>
        </Flash>
      ))}
    </>
  )
}
